import { getPVAUrl } from "@inrange/api-clients";
import {
  useOrganisationList,
  useSite,
} from "@inrange/building-manager-api-client";
import {
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteOwnership,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { AxiosError } from "axios";
import { ReactNode, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { QuarterYearPicker } from "../../../../components";
import {
  OFFTAKER_ONLY_STATUS_OPTIONS,
  getStatusOptionsForSite,
} from "../../operationalStatusUtils";
import { SITE_STATUS_LABELS } from "../enums";
import AddOwnership from "./AddOwnership";
import BelongsTo from "./BelongsTo";

const LockWithNoteModal = ({
  lockWithNote,
  cancel,
}: {
  lockWithNote: (note: string) => void;
  cancel: () => void;
}) => {
  const [lockNote, setLockNote] = useState("");
  return (
    <Modal show={true} onHide={cancel} centered size="lg">
      <Modal.Header>
        <Modal.Title>Lock this site?</Modal.Title>
      </Modal.Header>
      <Modal.Body id={"lockSiteWithNote"}>
        <div className="mb-3">
          Locking a site should be done at checkpoints when you have provided an
          offer. If you have done so, please add a link to that offer and an
          explanation in the notes field below.
        </div>
        <div>
          NOTE: Locking the site will generate an unlocked copy of the site.
          This copy will not include any applied SDM matches, uploaded Files,
          and will initially be owned by no organisations.
        </div>
        <div>
          <Form.Group className="mt-3 mb-3" controlId="lockNote">
            <Form.Control
              as="textarea"
              rows={2}
              value={lockNote}
              onChange={(e) => setLockNote(e.target.value)}
              autoFocus
              data-testid="site-lock-notes"
            />
          </Form.Group>
        </div>
        <div
          style={{
            display: "flex",
            gap: 24,
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          <Button variant="secondary" size="sm" onClick={cancel}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => lockWithNote(lockNote)}
            disabled={lockNote.trim().length === 0}
          >
            Lock site
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const SiteInformation = ({
  site,
  offtakerOnlySite,
  setSite,
  ownerships,
  onOwnerAdded,
  onOwnerRemoved,
  onOwnerEdited,
  onSaveClick,
  setNewSiteName,
  allowCopy,
  siteUpdates,
  isBulkEdit,
  setAlertStyle,
  setAlertMessage,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  offtakerOnlySite: boolean;
  ownerships: SiteOwnership[];
  onOwnerAdded: (ownership: {
    ownershipIndex: number;
    selectedID: string;
    selectedName: string;
    selectedType: string;
    selectedPark: string;
  }) => void;
  onOwnerRemoved?: (owner: { orgID: string; ownership: string }) => void;
  onOwnerEdited?: React.Dispatch<
    React.SetStateAction<Record<string, SiteOwnership>>
  >;
  onSaveClick?: (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => void;
  setNewSiteName: (name: string, ownerships: SiteOwnership[]) => void;
  allowCopy: boolean;
  siteUpdates?: RecursivePartialSiteAllowStringValues;
  isBulkEdit?: boolean;
  setAlertStyle: (style: string) => void;
  setAlertMessage: (
    message: ReactNode | string | undefined | undefined
  ) => void;
}) => {
  const { siteCalculations, errors } = useContextTS(SiteCalculationsContext);
  const [showBelongsTo, setShowBelongsTo] = useState(ownerships?.length > 0);
  const [copyDisabled, setCopyDisabled] = useState(false);
  const [showLockWithNoteModal, setShowLockWithNoteModal] = useState(false);
  const queryClient = useQueryClient();
  const { fetchOrganisations } = useOrganisationList();

  const epcOrgs = useMemo(
    () =>
      (fetchOrganisations.data?.organisations || [])
        .filter((org) => org.orgType === "EPC")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [fetchOrganisations.data?.organisations]
  );

  const currentEpcOwnership = site.siteOwnerships?.find(
    (so) => so.ownership === "epc"
  );

  const sdmExcludingOrgs = useMemo(() => {
    const allOrgs = fetchOrganisations.data?.organisations ?? [];
    const ownershipOrgIds = new Set(
      (site.siteOwnerships ?? []).map((so) => so.orgID)
    );
    return allOrgs
      .filter(
        (org) => ownershipOrgIds.has(org.id) && org.sdmExcludeFromMatching
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [fetchOrganisations.data?.organisations, site.siteOwnerships]);

  const handleEpcChange = (orgID: string) => {
    if (!onSaveClick) return;
    if (orgID === "") {
      // Remove EPC ownership
      if (currentEpcOwnership) {
        onOwnerRemoved?.({
          orgID: currentEpcOwnership.orgID,
          ownership: "epc",
        });
      }
      setSite({ oAndMContactEmail: null });
    } else {
      const selectedOrg = epcOrgs.find((org) => org.id === orgID);
      if (!selectedOrg) return;
      if (currentEpcOwnership) {
        // Remove old EPC first
        onOwnerRemoved?.({
          orgID: currentEpcOwnership.orgID,
          ownership: "epc",
        });
      }
      onOwnerAdded({
        ownershipIndex: -1,
        selectedID: orgID,
        selectedName: selectedOrg.name,
        selectedType: "epc",
        selectedPark: "",
      });
    }
  };

  const parseCoordinates = (lat: string, lng: string) => {
    const parsedLat = parseFloat(lat);
    const parsedLong = parseFloat(lng);
    return `${parsedLat},${parsedLong}`;
  };

  const operationalStatusOptions = offtakerOnlySite
    ? OFFTAKER_ONLY_STATUS_OPTIONS
    : getStatusOptionsForSite(site.operationalStatus ?? "notStarted");

  const { copySite } = useSite({ app: "admin" });

  const doCopySite = async () => {
    setCopyDisabled(true);
    copySite.mutate({
      siteId: site.id,
      copyBody: { pvaUrl: getPVAUrl(), site: siteUpdates },
    });
    setAlertStyle("secondary");
    setAlertMessage("Duplicating site...");
  };
  if (copySite.isError) {
    setCopyDisabled(false);
    const error = copySite.error as AxiosError;
    const errorMessage = error.response?.data?.["message"] || error.message;
    setAlertStyle("danger");
    setAlertMessage(`Error duplicating site - ${errorMessage}`);
    copySite.reset();
  }
  if (copySite.isSuccess) {
    setCopyDisabled(false);
    const response = copySite.data;
    // noopener is required: the Kinde v5 provider's init closes any tab that
    // loads the app with a same-origin window.opener (it assumes such a tab is a
    // leftover auth popup - `finally { hasSameOriginOpener() && window.close() }`).
    // Without noopener the duplicated site's tab opens and is immediately closed.
    window.open(`/site/${response.site_id}/edit`, "_blank", "noopener,noreferrer");
    setAlertMessage(undefined);
    copySite.reset();
    queryClient.invalidateQueries(["site", site.id]);
  }

  return (
    <Card body>
      {onSaveClick && showLockWithNoteModal && (
        <LockWithNoteModal
          lockWithNote={(note: string) => {
            setShowLockWithNoteModal(false);
            onSaveClick({
              site: { locked: true, lockNote: note },
              pvaUrl: getPVAUrl(),
            });
          }}
          cancel={() => setShowLockWithNoteModal(false)}
        />
      )}
      <div style={{ display: "flex", flexDirection: "row" }}>
        <Form.Label>
          <strong>{site.locked ? "🔒 " : ""}Site information</strong>
        </Form.Label>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          {allowCopy &&
            onSaveClick &&
            !site.locked &&
            !site.operationalStatus?.startsWith("operational") && (
              <Button
                variant="inrangesecondary"
                onClick={() => setShowLockWithNoteModal(true)}
              >
                Lock site
              </Button>
            )}
          {allowCopy && (
            <Button
              variant="inrangesecondary"
              onClick={doCopySite}
              disabled={copyDisabled}
            >
              Duplicate site
            </Button>
          )}
        </div>
      </div>

      {!isBulkEdit && (
        <Row className="d-flex flex-wrap align-items-end">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="siteName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={site.name}
                onChange={(e) => setSite({ name: e.target.value })}
              />
            </Form.Group>
          </Col>

          <Col sm={6}>
            <fieldset disabled={site.locked}>
              <Form.Group className="mb-3" controlId="address">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={site.address}
                  onChange={(e) => setSite({ address: e.target.value })}
                />
              </Form.Group>
            </fieldset>
          </Col>
        </Row>
      )}
      <fieldset disabled={site.locked}>
        <Row className="d-flex flex-wrap">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId="score">
              <Form.Label>Score</Form.Label>
              <Form.Select
                value={site.score || ""}
                onChange={(e) =>
                  setSite({
                    score:
                      "" === e.target.value ? null : parseFloat(e.target.value),
                    // A manual score change invalidates the persisted factor
                    // explanation; clear it so the dashboard tooltip falls back
                    // to the generic "score of N" copy until the next accept.
                    scoreFactors: [],
                  })
                }
              >
                {[
                  ["", "No score"],
                  [1, 1],
                  [2, 2],
                  [3, 3],
                  [4, 4],
                  [5, 5],
                ].map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {!isBulkEdit && (
            <>
              <Col sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>LatLng</Form.Label>
                  <Form.Control
                    disabled
                    value={parseCoordinates(
                      siteCalculations.latitude as unknown as string,
                      siteCalculations.longitude as unknown as string
                    )}
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col sm={6}>
            <Form.Group className="mb-3" controlId={"investmentModel"}>
              <Form.Label>Lease/License</Form.Label>
              <Form.Select
                value={site.investmentModel ?? "none"}
                onChange={(e) => setSite({ investmentModel: e.target.value })}
              >
                {Object.keys(SITE_STATUS_LABELS).map((key) => (
                  <option key={key} value={key}>
                    {SITE_STATUS_LABELS[key]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId={"operationalStatus"}>
              <Form.Label>Operational status</Form.Label>
              <Form.Select
                value={site.operationalStatus}
                onChange={(e) =>
                  setSite({
                    operationalStatus: e.target.value,
                  })
                }
                isInvalid={!!errors.operationalStatus}
              >
                {Object.entries(operationalStatusOptions).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  )
                )}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.operationalStatus}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group
              className={"mb-3"}
              controlId={"commerciallyOperationalDate"}
            >
              <Form.Label>
                {site.operationalStatus?.startsWith("operational")
                  ? "Commercially operational date"
                  : "Est. commercially operational date"}
              </Form.Label>
              <Form.Control
                style={{
                  color: site.commerciallyOperationalDate ? "black" : "grey",
                }}
                type="date"
                onClick={(e) => {
                  const element = e.target as HTMLInputElement;
                  if ("showPicker" in HTMLInputElement.prototype) {
                    // showPicker() is supported. Chrome 99+ and Safari 16+
                    // see https://caniuse.com/?search=showPicker
                    element.showPicker();
                  }
                }}
                value={site.commerciallyOperationalDate || ""}
                onChange={(e) =>
                  setSite({
                    commerciallyOperationalDate: e.target.value || null,
                  })
                }
                isInvalid={!!errors.commerciallyOperationalDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.commerciallyOperationalDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className={"mb-3"} controlId={"modellingFromDate"}>
              <Form.Label>Forecasting start date</Form.Label>
              <Form.Control
                disabled={!!site.commerciallyOperationalDate}
                style={{
                  color: "black",
                }}
                type="date"
                onClick={(e) => {
                  const element = e.target as HTMLInputElement;
                  if ("showPicker" in HTMLInputElement.prototype) {
                    // showPicker() is supported. Chrome 99+ and Safari 16+
                    // see https://caniuse.com/?search=showPicker
                    element.showPicker();
                  }
                }}
                value={site.modellingFromDate || ""}
                onChange={(e) => {
                  if (e.target.value) {
                    setSite({ modellingFromDate: e.target.value });
                  }
                }}
                isInvalid={!!errors.modellingFromDate}
              />
            </Form.Group>
            <Form.Control.Feedback type="invalid">
              {errors.modellingFromDate}
            </Form.Control.Feedback>
          </Col>
          {site.investmentModel === "lease" && (
            <Col sm={6}>
              <Form.Group className={"mb-3"} controlId={"funderStartDate"}>
                <Form.Label>SPV start date</Form.Label>
                  <QuarterYearPicker
                    value={
                      site.funderStartDate
                        ? new Date(site.funderStartDate)
                        : null
                    }
                    onChange={(date) => {
                      if (date) {
                        const year = date.getFullYear();
                        const month = date.getMonth() + 1;
                        const paddedMonth = month.toString().padStart(2, "0");
                        setSite({
                          funderStartDate: `${year}-${paddedMonth}-01`,
                        });
                      }
                    }}
                    disabled={site.locked}
                    wrapperClassName="w-100"
                  />
              </Form.Group>
              <Form.Control.Feedback type="invalid">
                {errors.funderStartDate}
              </Form.Control.Feedback>
            </Col>
          )}
          <Col sm={6}>
            <Form.Group className="mb-3" controlId={"assignedEpc"}>
              <Form.Label>
                {currentEpcOwnership ? (
                  <strong>
                    <Link
                      to={`/organisation/${currentEpcOwnership.orgID}/edit`}
                      style={{ color: "black", textDecoration: "none" }}
                    >
                      Assigned EPC
                    </Link>
                  </strong>
                ) : (
                  "Assigned EPC"
                )}
              </Form.Label>
              <Form.Select
                value={currentEpcOwnership?.orgID || ""}
                onChange={(e) => handleEpcChange(e.target.value)}
              >
                <option value="">None</option>
                {epcOrgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {currentEpcOwnership && (
            <Col sm={6}>
              <Form.Group className="mb-3" controlId={"oAndMContactEmail"}>
                <Form.Label>O&M contact email</Form.Label>
                <Form.Control
                  type="email"
                  value={site.oAndMContactEmail || ""}
                  onChange={(e) =>
                    setSite({ oAndMContactEmail: e.target.value || null })
                  }
                  placeholder={
                    epcOrgs.find(
                      (org) => org.id === currentEpcOwnership.orgID
                    )?.defaultOAndMEmail || ""
                  }
                />
              </Form.Group>
            </Col>
          )}
        </Row>
        <Row className="d-flex flex-wrap">
          <Col sm={6}>
            <Form.Group className="mb-3" controlId={"sdmExcludeFromMatching"}>
              <Form.Check // prettier-ignore
                type={"checkbox"}
                label={"Don't SDM match to this site"}
                checked={site.sdmExcludeFromMatching}
                onChange={(e) =>
                  setSite({ sdmExcludeFromMatching: e.target.checked })
                }
              />
              {sdmExcludingOrgs.length > 0 && (
                <Form.Text
                  className="text-muted"
                  style={{ display: "block", fontSize: "0.85em" }}
                >
                  Already excluded by linked organisation:{" "}
                  {sdmExcludingOrgs.map((org) => org.name).join(", ")}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3" controlId={"sdmHideMatches"}>
              <Form.Check // prettier-ignore
                type={"checkbox"}
                label={"Don't show SDM matches on this site"}
                checked={site.sdmHideMatches}
                onChange={(e) => setSite({ sdmHideMatches: e.target.checked })}
              />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>
      <Card>
        {showBelongsTo && (
          <>
            <BelongsTo
              siteID={site.id}
              ownerships={ownerships}
              onOwnerRemoved={onOwnerRemoved!}
              onOwnerEdited={onOwnerEdited!}
              setShowBelongsTo={setShowBelongsTo}
            />
            <hr style={{ margin: "4px 0" }} />
          </>
        )}
        <AddOwnership
          site={site}
          setSite={setSite}
          onOwnerAdded={onOwnerAdded}
          ownerships={ownerships}
          setNewSiteName={setNewSiteName}
          error={errors.siteOwnerships ?? ""}
        />
      </Card>
    </Card>
  );
};

export default SiteInformation;
